import { injectable, interfaces } from 'inversify';
import { action, makeObservable, observable } from 'mobx';
import { v4 } from 'uuid';
import { Parameters } from '../../__legacy__/helpers/parameters.helpers';
import { IPagingInfoWithOrder, PaginationInfoDefault } from '../../__legacy__/models/paginationInfo.model';
import { IAnnotationType } from '../../__legacy__/modules/annotation/submodules/annotationTypes/models/annotationType';
import { IChartPoint, IPrecisionRecall, ITrainingMatrix, ITrainingMetrics, ITrainingResult } from './jobMetrics.model';
import { IPredictionImage } from './jobPredictions.model';
import {
  IBooleanError,
  IJobAnnotationTool,
  IJobDataset,
  IJobProject,
  IJobStartingProgress,
  INFERENCE_TYPE,
  IStringError,
  ISubTrainingJob,
  JOB_STATUS,
  JOB_TYPE,
  TRAINING_TYPE,
  TRAINING_VARIANT,
} from './jobs.model';

interface IJobDetailsValidationErrors {
  showAll: boolean;
  settings: IStringError;
  header: IStringError;
  custom: IStringError;
  datasets: IBooleanError;
  images: IBooleanError;
  annotationTools: IBooleanError;
  training: IBooleanError;
}

interface IJobImagesPaging extends IPagingInfoWithOrder {
  fetchPredictions: boolean;
}

interface IJobDetailsStoreBase {
  storeVersion: string;
  jobType: JOB_TYPE | null;
  job: {
    id: string | null;
    isOwner: boolean;
    name: string;
    description: string;
    status: JOB_STATUS;
    startingProgress: IJobStartingProgress;
    failureReason: string | null;
    variant: TRAINING_VARIANT | null;
    datasetIds: string[];
    annotationToolIds: string[];
    config: Record<string, unknown>;
    dateCreated: string | null;
    calculatedCost: number;
    startingAdditionalInfo: string[];
    projectId: string | null;
    type: TRAINING_TYPE | INFERENCE_TYPE | null;
    parentTrainingId: string | null;
    trainingProjectId: string | null;
    trainingId: string;
    isTrainingOwner: boolean;
    username: string | null;
    trainingTime: number;
  };
  dataSources: {
    projects: IJobProject[];
    datasets: IJobDataset[];
    trainings: Record<'value' | 'label', string>[];
    datasetsInWorkspace: IJobDataset[];
  };
  images: {
    images: IPredictionImage[];
    paging: IJobImagesPaging;
  };
  metrics: {
    metrics: ITrainingMetrics;
    results: ITrainingResult[];
    matrix: ITrainingMatrix;
    precisionRecall: IPrecisionRecall | null;
    loss: IChartPoint[];
    validationAccuracy: IChartPoint[];
    trainingAccuracy: IChartPoint[];
  };
  params: Parameters;
  validationErrors: IJobDetailsValidationErrors;
  estimatedCost: number | null;
  annotationTypes: IAnnotationType[];
  annotationTools: IJobAnnotationTool[];
  trainingJobs: ISubTrainingJob[];
  resetStore(isDraft: boolean, jobType: JOB_TYPE): void;
}

type ITrainingDetailsStore = IJobDetailsStoreBase & {
  jobType: JOB_TYPE.TRAINING;
  job: {
    projectId: string;
    type: TRAINING_TYPE;
    trainingProjectId: never;
    trainingId: never;
    isTrainingOwner: never;
  };
  dataSources: {
    models: never;
    datasetsInWorkspace: never;
  };
};

type IInferenceDetailsStore = IJobDetailsStoreBase & {
  jobType: JOB_TYPE.INFERENCE;
  job: {
    projectId: string | null;
    type: INFERENCE_TYPE;
    parentTrainingId: never;
  };
  metrics: {
    loss: never;
    validationAccuracy: never;
    trainingAccuracy: never;
  };
  annotationTools: never;
  trainingJobs: never;
};

export type IJobDetailsStore = IInferenceDetailsStore | ITrainingDetailsStore;

const defaultJobImagesPaging: IJobImagesPaging = {
  ...PaginationInfoDefault,
  orderBy: 'name',
  orderType: 'asc',
  search: '',
  fetchPredictions: false,
};

export const JOB_DETAILS_STORE_INITIAL_STATE: Omit<IJobDetailsStoreBase, 'resetStore' | 'storeVersion'> = {
  jobType: null,
  job: {
    id: null,
    isOwner: false,
    name: '',
    description: '',
    status: JOB_STATUS.UNKNOWN,
    startingProgress: null,
    failureReason: null,
    variant: null,
    projectId: null,
    datasetIds: [],
    config: {},
    dateCreated: null,
    calculatedCost: 0,
    annotationToolIds: [],
    type: null,
    parentTrainingId: null,
    startingAdditionalInfo: [],
    trainingProjectId: null,
    trainingId: '',
    isTrainingOwner: false,
    username: null,
    trainingTime: 0,
  },
  images: {
    images: [],
    paging: defaultJobImagesPaging,
  },
  metrics: {
    results: [],
    matrix: { data: [], fields: [] },
    metrics: {
      auc: null,
    },
    loss: [],
    validationAccuracy: [],
    trainingAccuracy: [],
    precisionRecall: null,
  },
  dataSources: {
    projects: [],
    datasets: [],
    datasetsInWorkspace: [],
    trainings: [],
  },
  params: [],
  validationErrors: {
    showAll: false,
    settings: new Map(),
    header: new Map(),
    custom: new Map(),
    datasets: undefined,
    images: undefined,
    annotationTools: undefined,
    training: undefined,
  },
  estimatedCost: null,
  annotationTools: [],
  trainingJobs: [],
  annotationTypes: [],
};

export namespace IJobDetailsStore {
  export const $: interfaces.ServiceIdentifier<IJobDetailsStore> = Symbol('JOB_DETAILS_STORE');
}

@injectable()
export class JobDetailsStore implements IJobDetailsStoreBase {
  storeVersion;
  dataSources;
  images;
  metrics;
  params;
  validationErrors;
  estimatedCost;
  annotationTools;
  trainingJobs;
  annotationTypes;
  jobType: IJobDetailsStoreBase['jobType'];
  job;

  constructor() {
    makeObservable(this, {
      storeVersion: observable,
      dataSources: observable,
      images: observable,
      metrics: observable,
      params: observable,
      validationErrors: observable,
      estimatedCost: observable,
      annotationTools: observable,
      trainingJobs: observable,
      annotationTypes: observable,
      jobType: observable,
      job: observable,
      resetStore: action.bound,
    });

    this.storeVersion = v4();
    this.jobType = JOB_TYPE.UNKNOWN;
    this.job = { ...JOB_DETAILS_STORE_INITIAL_STATE.job };
    this.images = { ...JOB_DETAILS_STORE_INITIAL_STATE.images };
    this.metrics = { ...JOB_DETAILS_STORE_INITIAL_STATE.metrics };
    this.dataSources = { ...JOB_DETAILS_STORE_INITIAL_STATE.dataSources };
    this.params = [...JOB_DETAILS_STORE_INITIAL_STATE.params];
    this.validationErrors = { ...JOB_DETAILS_STORE_INITIAL_STATE.validationErrors };
    this.estimatedCost = JOB_DETAILS_STORE_INITIAL_STATE.estimatedCost;
    this.annotationTools = [...JOB_DETAILS_STORE_INITIAL_STATE.annotationTools];
    this.trainingJobs = [...JOB_DETAILS_STORE_INITIAL_STATE.trainingJobs];
    this.annotationTypes = [...JOB_DETAILS_STORE_INITIAL_STATE.annotationTypes];
  }

  resetStore(isDraft: boolean, jobType: JOB_TYPE): void {
    this.storeVersion = v4();
    Object.assign<IJobDetailsStoreBase, Omit<IJobDetailsStoreBase, 'resetStore' | 'storeVersion'>>(this, { ...JOB_DETAILS_STORE_INITIAL_STATE });
    this.job.status = isDraft ? JOB_STATUS.DRAFT : JOB_DETAILS_STORE_INITIAL_STATE.job.status;

    // Type specific inititalizations
    if (jobType === JOB_TYPE.TRAINING && isDraft) {
      this.job.type = TRAINING_TYPE.MANUAL;
    }

    if (jobType === JOB_TYPE.INFERENCE && isDraft) {
      this.job.type = INFERENCE_TYPE.PROJECT;
    }

    this.jobType = jobType;
    this.job.isOwner = isDraft ? true : JOB_DETAILS_STORE_INITIAL_STATE.job.isOwner;
    this.job.name = isDraft ? `${jobType === JOB_TYPE.INFERENCE ? 'Evaluation' : 'Model'} ${new Date().toLocaleString()}` : '';
  }
}
